import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, BookOpen, ChevronDown, Sparkles } from 'lucide-react';
import useCoachingResources, { coachingPageUrl } from '../hooks/useCoachingResources';
import { quizTitle, quizSummary } from '../data/quizTitles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : '');

const Result = ({ q, resources }) => {
  const [open, setOpen] = useState(false);
  const linked = resources.filter((r) => q.resource_ids.includes(r.id));
  return (
    <div className="bg-white border border-stone-200 rounded-xl" data-testid={`my-quiz-${q.quiz_id}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid={`my-quiz-toggle-${q.quiz_id}`}>
        <div className="flex-1 min-w-0">
          <p className="font-heading text-base text-forest">{quizTitle(q.quiz_type)}</p>
          <p className="font-body text-xs text-stone-500">{fmt(q.created_at)}{quizSummary(q) ? ` · ${quizSummary(q)}` : ''}</p>
        </div>
        <ChevronDown size={16} className={`text-stone-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-4">
          {q.recommendations && (
            <div>
              <p className="font-body text-[11px] uppercase tracking-wider text-[#8a6a5a] mb-1">Jill’s recommendations</p>
              <p className="font-body text-sm text-stone-700 whitespace-pre-wrap leading-relaxed" data-testid={`my-quiz-recs-${q.quiz_id}`}>{q.recommendations}</p>
            </div>
          )}
          {q.follow_up && (
            <div>
              <p className="font-body text-[11px] uppercase tracking-wider text-[#8a6a5a] mb-1">Next steps</p>
              <p className="font-body text-sm text-stone-700 whitespace-pre-wrap leading-relaxed" data-testid={`my-quiz-followup-${q.quiz_id}`}>{q.follow_up}</p>
            </div>
          )}
          {linked.length > 0 && (
            <div>
              <p className="font-body text-[11px] uppercase tracking-wider text-[#8a6a5a] mb-1.5">Your resources</p>
              <ul className="space-y-1.5">
                {linked.map((r) => (
                  <li key={r.id}>
                    <Link to={coachingPageUrl(r.id)} className="inline-flex items-center gap-1.5 font-body text-sm text-forest hover:text-terracotta underline decoration-dotted decoration-terracotta/50 underline-offset-4" data-testid={`my-quiz-resource-${q.quiz_id}-${r.id}`}>
                      <BookOpen size={13} className="text-terracotta" /> {r.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {q.quiz_type === 'food_preferences' && (
            <Link to="/resources/recipes" className="inline-flex items-center gap-1.5 font-body text-sm text-terracotta hover:underline" data-testid={`my-quiz-recipes-${q.quiz_id}`}>
              <Sparkles size={13} /> See recipes matched to your foods
            </Link>
          )}
          {!q.recommendations && !q.follow_up && !linked.length && q.quiz_type !== 'food_preferences' && (
            <p className="font-body text-xs text-stone-400 italic">Jill will talk this one through with you in session.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default function MyQuizResults({ userId }) {
  const { resources } = useCoachingResources();
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!userId) return;
    fetch(`${API}/my-quiz-results?user_id=${encodeURIComponent(userId)}`).then((r) => (r.ok ? r.json() : { results: [] }))
      .then((d) => setResults(d.results || [])).catch(() => {});
  }, [userId]);
  if (!results.length) return null;
  return (
    <section className="mb-8" data-testid="my-quiz-results">
      <div className="flex items-center gap-2 mb-3">
        <ClipboardList size={16} className="text-[#8a6a5a]" />
        <h2 className="font-heading text-xl text-forest">My quiz results</h2>
      </div>
      <div className="space-y-2">
        {results.map((q) => <Result key={q.quiz_id} q={q} resources={resources} />)}
      </div>
    </section>
  );
}
